/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { createApi } from '@reduxjs/toolkit/query/react'
import { ApiResponse } from '../types/api';
import { axiosBaseQuery } from './axiosQuery';
import { SurveyActionData } from '../model/surveyData';
import { DataPeriods } from '../types/universalTracker';
import { SafeUser } from '../constants/users';

export interface StatusStats {
  created: number,
  updated: number,
  rejected: number,
  verified: number
}

export interface DelegationStats {
  contributor: number;
  updated: number;
  verifier: number;
  verified: number;
}
export interface UserWithStats extends SafeUser {
  lastLogin?: string;
  status?: StatusStats;
}

export interface UserWithDelegation extends Omit<UserWithStats, 'status'> {
  contributor?: StatusStats;
  verifier?: StatusStats;
  delegationStats?: DelegationStats;
}

export interface SurveyStats extends Pick<SurveyActionData, '_id' | 'name' | 'effectiveDate' | 'initiativeId' | 'completedDate'> {
  lastUpdated: string;
  period?: DataPeriods;
  initiativeName: string;
  status: StatusStats,
}

export interface InitiativeSurveyStats {
  _id: string,
  parentId?: string;
  name: string,
  status: StatusStats,
  surveys: SurveyStats[]
}

interface UserSurveyStats {
  user: SafeUser,
  surveys: SurveyStats[]
}

export interface QueryStats {
  initiativeId: string;
  isCompleted?: boolean;
  startDate?: Date;
  endDate?: Date;
  blueprint?: string;
}

// Define a service using a base URL and expected endpoints
export const initiativeApi = createApi({
  reducerPath: 'initiativeApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['stats', 'user-stats'],
  endpoints: (builder) => ({
    getStats: builder.query<InitiativeSurveyStats[], QueryStats>({
      transformResponse: (response: ApiResponse<InitiativeSurveyStats[]>) => response.data,
      providesTags: ['stats'],
      query: ({ initiativeId, ...params}) => ({
        url: `/initiatives/${initiativeId}/stats`,
        method: 'get',
        params: params,
      }),
    }),
    getUserSurveyStats: builder.query<UserSurveyStats, QueryStats & { userId: string }>({
      transformResponse: (response: ApiResponse<UserSurveyStats>) => response.data,
      query: ({ initiativeId, userId, ...params}) => ({
        url: `/initiatives/${initiativeId}/stats/users/${userId}`,
        method: 'get',
        params: params,
      }),
    }),
    getUserSurveyDetails: builder.query<SurveyActionData, QueryStats & { userId: string, surveyId: string }>({
      transformResponse: (response: ApiResponse<SurveyActionData>) => response.data,
      query: ({ initiativeId, userId, surveyId, ...params}) => ({
        url: `/initiatives/${initiativeId}/stats/users/${userId}/surveys/${surveyId}`,
        method: 'get',
        params: params,
      }),
    }),
    getUserStats: builder.query<UserWithDelegation[], QueryStats>({
      transformResponse: (response: ApiResponse<UserWithDelegation[]>) => response.data,
      providesTags: ['user-stats'],
      query: ({ initiativeId, ...params}) => ({
        url: `/initiatives/${initiativeId}/stats/users`,
        method: 'get',
        params: params,
      }),
    }),
  }),
})

export const { useGetStatsQuery, useGetUserStatsQuery, useGetUserSurveyDetailsQuery, useGetUserSurveyStatsQuery } = initiativeApi
